import { Immutable } from "immer";
import React, { type JSX } from "react";
import { t } from "ttag";

import { StoryTile } from "../../models/tempur-love-grid";
import { CategoriesFooter } from "../CategoriesFooter";
import { useImageLoadRef } from "../GridTile";

type Props = Immutable<{
    data: StoryTile;
}> & {
    ref?: React.Ref<HTMLAnchorElement>;
};

export const TempurLoveStoryPageTile = (props: Props): JSX.Element => {
    const imgRef = React.useRef<HTMLImageElement | null>(null);
    const onImageLoadRef = useImageLoadRef();
    const hasCalledLoadRef = React.useRef(false);

    const handleImageLoad = React.useCallback(() => {
        if (!hasCalledLoadRef.current) {
            hasCalledLoadRef.current = true;
            onImageLoadRef.current?.();
        }
        // onImageLoadRef is a stable ref - safe to omit from deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle images that are already loaded (SSR/cached images don't fire onload)
    React.useEffect(() => {
        if (!props.data.value.tile_image_url) {
            handleImageLoad();
        } else if (imgRef.current?.complete) {
            handleImageLoad();
        }
    }, [props.data.value.tile_image_url, handleImageLoad]);

    let avatar: JSX.Element | null = null;
    if (props.data.value.avatar_url) {
        avatar = (
            <>
                <figure className="avatar">
                    <img
                        className="avatar__image"
                        src={props.data.value.avatar_url}
                        alt={props.data.value.author}
                        loading="lazy"
                        decoding="async"
                    />
                </figure>
                <span>{props.data.value.author}</span>
            </>
        );
    }

    return (
        <>
            <span className="post__banner">{props.data.value.tile_banner}</span>
            <div className="post__main-image-container">
                {props.data.value.tile_image_url ? (
                    <img
                        ref={imgRef}
                        className="post__main-image responsive-img"
                        src={props.data.value.tile_image_url}
                        alt={props.data.value.tile_title}
                        onLoad={handleImageLoad}
                        onError={handleImageLoad}
                        loading="lazy"
                        decoding="async"
                    />
                ) : null}
            </div>
            <div className="post__avatar-container">{avatar}</div>
            <section className="post-copy">
                <h2 className="post-copy__title">
                    {props.data.value.tile_title}
                </h2>
                <div
                    className="post-copy__body"
                    dangerouslySetInnerHTML={{
                        __html: props.data.value.tile_body,
                    }}
                />
                <a
                    href={props.data.value.url}
                    ref={props.ref}
                    className="post-link"
                >
                    {t`Read more`}
                </a>

                <CategoriesFooter
                    categories={props.data.value.categories}
                    city={props.data.value.city}
                    state={props.data.value.state}
                />
            </section>
        </>
    );
};
